import React from 'react';
import { CheckCircle, Calendar, Users, Award } from 'lucide-react';

const OurEvolutionPage = () => {
  return (
    <div className="flex flex-col">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-indigo-900 via-purple-800 to-blue-700 text-white py-20 md:py-32">
        <div className="container mx-auto px-4 relative">
          <div className="max-w-4xl">
            {/* <div className="mb-6">
              <span className="inline-block bg-yellow-400 text-black px-4 py-2 rounded-md text-sm font-bold uppercase tracking-wide">
                Our Evolution
              </span>
            </div> */}
            <h1 className="text-4xl md:text-6xl font-bold mb-6 leading-tight">
              Our Journey
            </h1>
            <p className="text-xl md:text-3xl mb-8 leading-relaxed opacity-90">
              Founded to bridge global innovation with India’s excellence
            </p>
          </div>
        </div>
      </section>

      {/* Evolution Timeline */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">Our Growth Story</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              A structured path from foundation to launch, creating excellence at every step.
            </p>
          </div>

          <div className="space-y-12">
            {/* 2024 – Groundwork */}
            <div className="flex flex-col lg:flex-row items-center gap-8">
              <div className="lg:w-1/3">
                <div className="bg-orange-100 p-6 rounded-lg">
                  <span className="text-4xl mb-4 block">🚀</span>
                  <h3 className="text-2xl font-bold text-orange-800 mb-2">2024</h3>
                  <p className="text-orange-700">Groundwork</p>
                </div>
              </div>
              <div className="lg:w-2/3">
                <h4 className="text-2xl font-bold mb-4 text-red-600">Laying the Foundation</h4>
                <p className="text-gray-700 mb-4">
                  We validated the model with early pilot engagements, built our hiring network in India, and set up the operating rails for compliance, payroll and vendor partnerships.
                </p>
                <div className="space-y-2">
                  <div className="flex items-center">
                    <CheckCircle className="text-green-500 mr-2" size={16} />
                    <span className="text-sm text-gray-600">Hiring playbooks & interview loops established</span>
                  </div>
                  <div className="flex items-center">
                    <CheckCircle className="text-green-500 mr-2" size={16} />
                    <span className="text-sm text-gray-600">Initial partner & talent networks onboarded</span>
                  </div>
                  <div className="flex items-center">
                    <CheckCircle className="text-green-500 mr-2" size={16} />
                    <span className="text-sm text-gray-600">Delivery quality gates & onboarding checklists drafted</span>
                  </div>
                </div>
              </div>
            </div>

            {/* 2025 – Launch */}
            <div className="flex flex-col lg:flex-row-reverse items-center gap-8">
              <div className="lg:w-1/3">
                <div className="bg-blue-100 p-6 rounded-lg">
                  <Calendar className="text-blue-600 mb-4" size={32} />
                  <h3 className="text-2xl font-bold text-blue-800 mb-2">2025</h3>
                  <p className="text-blue-700">Launch</p>
                </div>
              </div>
              <div className="lg:w-2/3">
                <h4 className="text-2xl font-bold mb-4 text-red-600">From Vision to Execution</h4>
                <p className="text-gray-700 mb-4">
                  EXCESSD formally launched, onboarding our first customer and standing up the first dedicated team with full transparency on process, cost, and delivery.
                </p>
                <div className="space-y-2">
                  <div className="flex items-center">
                    <CheckCircle className="text-green-500 mr-2" size={16} />
                    <span className="text-sm text-gray-600">First dedicated team activated with seamless onboarding</span>
                  </div>
                  {/* <div className="flex items-center">
                    <CheckCircle className="text-green-500 mr-2" size={16} />
                    <span className="text-sm text-gray-600">Core services launched: Full-stack, QA automation, DevOps</span>
                  </div> */}
                  <div className="flex items-center">
                    <CheckCircle className="text-green-500 mr-2" size={16} />
                    <span className="text-sm text-gray-600">Operating model: rates, compliance, and SLAs standardized</span>
                  </div>
                </div>
              </div>
            </div>

            {/* 2025 – Expansion */}
            <div className="flex flex-col lg:flex-row items-center gap-8">
              <div className="lg:w-1/3">
                <div className="bg-green-100 p-6 rounded-lg">
                  <Users className="text-green-600 mb-4" size={32} />
                  <h3 className="text-2xl font-bold text-green-800 mb-2">2025</h3>
                  <p className="text-green-700">Expansion</p>
                </div>
              </div>
              <div className="lg:w-2/3">
                <h4 className="text-2xl font-bold mb-4 text-red-600">Building Repeatable Excellence</h4>
                <p className="text-gray-700 mb-4">
                  We focused on creating a scalable and repeatable delivery model that ensures consistent quality, faster onboarding, and predictable outcomes for our clients.
                </p>
                <div className="space-y-2">
                  {/* <div className="flex items-center">
                    <CheckCircle className="text-green-500 mr-2" size={16} />
                    <span className="text-sm text-gray-600">Second dedicated pod; broader coverage across FE/BE, SRE, QA</span>
                  </div>
                  <div className="flex items-center">
                    <CheckCircle className="text-green-500 mr-2" size={16} />
                    <span className="text-sm text-gray-600">Candidate community & referral network scaled</span>
                  </div>
                  <div className="flex items-center">
                    <CheckCircle className="text-green-500 mr-2" size={16} />
                    <span className="text-sm text-gray-600">Security & IP protection practices aligned to ISO-27001 controls</span>
                  </div> */}
                </div>
              </div>
            </div>

            {/* 2026 – Roadmap */}
            <div className="flex flex-col lg:flex-row-reverse items-center gap-8">
              <div className="lg:w-1/3">
                <div className="bg-purple-100 p-6 rounded-lg">
                  <Award className="text-purple-600 mb-4" size={32} />
                  <h3 className="text-2xl font-bold text-purple-800 mb-2">2026</h3>
                  <p className="text-purple-700">Roadmap</p>
                </div>
              </div>
              <div className="lg:w-2/3">
                <h4 className="text-2xl font-bold mb-4 text-red-600">What’s Next</h4>
                <p className="text-gray-700 mb-4">
                  Our focus is sustainable, long-term growth - strengthening our talent pipelines, expanding our global reach, and continuing to mature our delivery systems, always with transparency and quality at the center.
                </p>
                <div className="space-y-2">
                  <div className="flex items-center">
                    <CheckCircle className="text-green-500 mr-2" size={16} />
                    <span className="text-sm text-gray-600">Expanding our capability depth to support long-term client growth</span>
                  </div>
                  <div className="flex items-center">
                    <CheckCircle className="text-green-500 mr-2" size={16} />
                    <span className="text-sm text-gray-600">Strengthening early-career pipelines through structured university engagement</span>
                  </div>
                  <div className="flex items-center">
                    <CheckCircle className="text-green-500 mr-2" size={16} />
                    <span className="text-sm text-gray-600">Formalizing long term team models that allow clients to build enduring engineering capacity</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Foundation */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">Built on Strong Foundations</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Relationships and results shaped by simple principles: clarity, craft, and consistency.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-xl shadow-sm text-center">
              <div className="bg-blue-100 p-4 rounded-full w-16 h-16 mx-auto mb-6 flex items-center justify-center">
                <span className="text-2xl">⚡</span>
              </div>
              <h3 className="text-xl font-bold mb-4 text-red-600">Proven Engineering Expertise</h3>
              <p className="text-gray-700">We engineer with precision, scale with confidence, and deliver with impact.</p>
            </div>
            
            <div className="bg-white p-8 rounded-xl shadow-sm text-center">
              <div className="bg-green-100 p-4 rounded-full w-16 h-16 mx-auto mb-6 flex items-center justify-center">
                <span className="text-2xl">📈</span>
              </div>
              <h3 className="text-xl font-bold mb-4 text-red-600">Adaptable Delivery Models</h3>
              <p className="text-gray-700">Flexible environment that evolve with your needs - ensuring speed, efficiency, and seamless scalability.</p>
            </div>
            
            <div className="bg-white p-8 rounded-xl shadow-sm text-center">
              <div className="bg-purple-100 p-4 rounded-full w-16 h-16 mx-auto mb-6 flex items-center justify-center">
                <span className="text-2xl">🎯</span>
              </div>
              <h3 className="text-xl font-bold mb-4 text-red-600">Quality Standards Alignment</h3>
              <p className="text-gray-700">Engineering excellence anchored in global standards - delivering reliability, compliance, and quality.</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default OurEvolutionPage;
